import type { CardStatusModalVariant } from '../components/CardStatusModal';
import type { NewCardField } from '../domain/errors';

/**
 * Falla al agregar una tarjeta que se informa con el modal de estado del diseño ("Tarjeta
 * inválida" / "Tarjeta expirada"). El resto de las fallas (campos del titular, CVV, DNI, red) siguen
 * como mensaje dentro del formulario.
 *
 * TODO(ENA-1607) [sin validar contra Payway]: hoy "expirada" y el número inválido se deciden con la
 * validación local (`validateNewCard`) y cualquier 4xx de `/tokens` es "inválida". Debería depender de
 * la respuesta de Payway según el campo rechazado (`CardTokenizationError.field`): vencimiento →
 * "expirada", número → "inválida", CVV/titular/DNI → mensaje en el formulario. Falta provocar errores
 * reales en el sandbox (número inválido, vencimiento pasado) para confirmar el formato de
 * `validation_errors` y sus `param` (el manual del repo no lo describe) y si Payway valida vencimientos
 * pasados; recién ahí reducir la validación local a formato y campos vacíos.
 */
export const addCardFailureVariant = (error: unknown): CardStatusModalVariant | undefined => {
  if (error instanceof NewCardValidationError) {
    if (error.fields.includes('expiration')) {
      return 'expired';
    }
    return error.fields.includes('cardNumber') ? 'invalid' : undefined;
  }
  if (error instanceof CardTokenizationError && error.failure === 'invalid-card') {
    return 'invalid';
  }
  return undefined;
};

/** Mensaje para el usuario ante una falla al agregar una tarjeta nueva. */
import { CardTokenizationError, NewCardValidationError, PaymentTokenError } from '../domain/errors';
import { translate } from '../i18n';

export type NewCardFieldErrors = Partial<Record<NewCardField, string>>;

/** Errores de validación por campo (titular, CVV, DNI, etc.). Vacío si el error es global o va en modal. */
export const addCardFieldErrors = (error: unknown): NewCardFieldErrors => {
  if (!(error instanceof NewCardValidationError)) {
    return {};
  }
  if (addCardFailureVariant(error)) {
    return {};
  }
  return error.fields.reduce<NewCardFieldErrors>((acc, field) => {
    acc[field] = translate(`paymentsUI.addCardForm.errors.${field}`);
    return acc;
  }, {});
};

export const addCardErrorMessage = (error: unknown): string => {
  if (error instanceof NewCardValidationError) {
    return translate(`paymentsUI.addCardForm.errors.${error.fields[0]}`);
  }
  if (error instanceof CardTokenizationError) {
    switch (error.failure) {
      case 'sdk-unavailable':
        return translate('paymentsUI.addCardForm.errors.sdkUnavailable');
      case 'timeout':
        return translate('paymentsUI.addCardForm.errors.timeout');
      case 'invalid-card':
        return translate('paymentsUI.addCardForm.errors.invalidCard');
      default:
        return translate('paymentsUI.addCardForm.errors.unknown');
    }
  }
  return translate('paymentsUI.addCardForm.errors.saveFailed');
};

/** Mensaje ante una falla al preparar el token de pago (antes de `initiate`: no hubo cobro). */
export const paymentTokenErrorMessage = (error: unknown): string => {
  if (error instanceof PaymentTokenError) {
    return translate(
      error.reason === 'missing-cvv'
        ? 'paymentsUI.errorBox.enterSecurityCode'
        : 'paymentsUI.errorBox.cardTokenExpired',
    );
  }
  return addCardErrorMessage(error);
};
